import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import React from "react";

import { useToast } from "@/components/ui/use-toast";
import { useConfirmDialog } from "@/context";

import * as clientsApi from "../clients.api";
import { TClientExtended } from "../clients.types";

export type SortField = "name" | "status" | "created_at";
export type SortDir = "asc" | "desc";

export const useSubPartners = (parentOrganizationId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { openDialog } = useConfirmDialog();
  const importFileRef = React.useRef<HTMLInputElement>(null);

  // ── Dados ──────────────────────────────────────────────────────────────────
  const { data: subPartners = [], isLoading } = useQuery<TClientExtended[]>({
    queryKey: ["sub-partners", parentOrganizationId],
    queryFn: () => clientsApi.getSubPartners(parentOrganizationId),
    enabled: !!parentOrganizationId,
  });

  // ── Filtro / Ordenação ─────────────────────────────────────────────────────
  const [search, setSearch] = React.useState("");
  const [sortField, setSortField] = React.useState<SortField>("name");
  const [sortDir, setSortDir] = React.useState<SortDir>("asc");
  const [filterStatus, setFilterStatus] = React.useState("all");
  const [filterType, setFilterType] = React.useState<"all" | "pj" | "pf">("all");

  const toggleSort = React.useCallback(
    (field: SortField) => {
      if (field === sortField) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortField(field);
        setSortDir("asc");
      }
    },
    [sortField]
  );

  const filteredAndSorted = React.useMemo(() => {
    let result = [...subPartners];

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          (p.cnpj || "").includes(q) ||
          ((p as any).cpf || "").includes(q) ||
          p.email?.toLowerCase().includes(q)
      );
    }

    if (filterStatus !== "all") {
      result = result.filter((p) => p.status === filterStatus);
    }

    if (filterType !== "all") {
      result = result.filter((p) =>
        filterType === "pj" ? !!p.cnpj : !!(p as any).cpf
      );
    }

    result.sort((a, b) => {
      const va = String((a as any)[sortField] ?? "");
      const vb = String((b as any)[sortField] ?? "");
      return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    });

    return result;
  }, [subPartners, search, sortField, sortDir, filterStatus, filterType]);

  // ── Paginação ─────────────────────────────────────────────────────────────
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);

  // Volta para página 1 sempre que filtro/busca mudar
  React.useEffect(() => {
    setPage(1);
  }, [search, filterStatus, filterType, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSorted.length / pageSize));
  const paginated = React.useMemo(
    () => filteredAndSorted.slice((page - 1) * pageSize, page * pageSize),
    [filteredAndSorted, page, pageSize]
  );

  const goToPage = React.useCallback(
    (p: number) => setPage(Math.min(Math.max(1, p), totalPages)),
    [totalPages]
  );

  const handlePageSizeChange = React.useCallback((size: number) => {
    setPageSize(size);
    setPage(1);
  }, []);

  // ── Exportar CSV ──────────────────────────────────────────────────────────
  const handleExportCsv = React.useCallback(async () => {
    try {
      const csv = await clientsApi.exportSubPartnersCsv(parentOrganizationId);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `parceiros-${format(new Date(), "yyyy-MM-dd")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Erro ao exportar CSV",
        description: (error as Error).message,
        variant: "destructive",
      });
    }
  }, [parentOrganizationId, toast]);

  // ── Importar XLSX/CSV ──────────────────────────────────────────────────────
  const [pendingRowCount, setPendingRowCount] = React.useState<number | null>(null);

  const importMutation = useMutation({
    mutationFn: (file: File) =>
      clientsApi.importSubPartnersCsv(parentOrganizationId, file),
    onSuccess: (result) => {
      setPendingRowCount(null);
      queryClient.invalidateQueries({ queryKey: ["sub-partners", parentOrganizationId] });
      const msg = `${result.created} criados, ${result.skipped} ignorados`;
      toast({
        title: "Importação concluída",
        description:
          result.errors.length > 0
            ? `${msg}. Erros: ${result.errors.slice(0, 3).join("; ")}`
            : msg,
      });
    },
    onError: (error: Error) => {
      setPendingRowCount(null);
      toast({
        title: "Erro na importação",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleImportFile = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";

      // Parse client-side to get row count for progress display
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const XLSX = require("xlsx") as typeof import("xlsx");
        const arrayBuffer = await file.arrayBuffer();
        const wb = XLSX.read(arrayBuffer, { type: "array" });
        const sheetName =
          wb.SheetNames.find((s: string) => s.toLowerCase() === "parceiros") ??
          wb.SheetNames[0];
        const ws = wb.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 }) as unknown[][];
        // data[0] is headers, rest are rows
        const dataRows = data.slice(1).filter((r: unknown[]) => r.some((c) => c !== ""));
        setPendingRowCount(dataRows.length);
      } catch {
        // If parse fails (e.g. CSV), show generic indicator
        setPendingRowCount(null);
      }

      importMutation.mutate(file);
    },
    [importMutation]
  );

  const triggerImport = React.useCallback(() => {
    importFileRef.current?.click();
  }, []);

  // ── Excluir ────────────────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: (subPartnerId: string) =>
      clientsApi.deleteSubPartner(subPartnerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sub-partners", parentOrganizationId] });
      toast({ title: "Parceiro excluído com sucesso" });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao excluir parceiro",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleDelete = React.useCallback(
    (partner: TClientExtended) => {
      openDialog(
        "Excluir parceiro",
        `Tem certeza que deseja excluir "${partner.name}"? Esta ação não pode ser desfeita.`,
        () => deleteMutation.mutate(partner.id)
      );
    },
    [openDialog, deleteMutation]
  );

  return {
    subPartners: paginated,
    allSubPartners: subPartners,
    filteredCount: filteredAndSorted.length,
    totalCount: subPartners.length,
    isLoading,
    // filtro
    search,
    setSearch,
    sortField,
    sortDir,
    toggleSort,
    filterStatus,
    setFilterStatus,
    filterType,
    setFilterType,
    // paginação
    page,
    pageSize,
    totalPages,
    goToPage,
    handlePageSizeChange,
    // export/import
    handleExportCsv,
    importMutation,
    pendingRowCount,
    handleImportFile,
    triggerImport,
    importFileRef,
    // delete
    deleteMutation,
    handleDelete,
  };
};
